/**
 * What of the page the screenshot clones. The library copies every computed style of every cloned element, so a long
 * page costs seconds although only the viewport is drawn. The plan keeps what touches the shot region and replaces the
 * rest so that the visible part keeps its layout:
 * - hidden (`display: none`) and out-of-flow elements outside the region are left out;
 * - in-flow elements after the last visible sibling and below the region are left out (the clone keeps the computed
 *   size of every element, so the parent keeps its height);
 * - other in-flow elements outside the region become stubs: the element without its content, pinned to its size;
 * - cells of a table whose rows were cut keep their widths, so the columns do not change.
 */

export interface Region {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface ShotPlan {
  /** Left out with their subtree. */
  skip: Set<Element>;
  /** Kept without content. */
  stubs: Set<Element>;
  /** Elements given the pin attribute (cleaned after the shot). */
  pinned: Element[];
  /** Images that stay in the shot. */
  images: HTMLImageElement[];
}

export const PIN_ATTR = 'data-bug-pin';

// Their content is drawn by the element itself or laid out by rules the plan does not follow.
const WHOLE = new Set(['svg', 'select', 'datalist', 'picture', 'video', 'audio', 'object', 'iframe', 'canvas', 'textarea', 'math', 'ruby']);
const TABLE_PARTS = new Set(['table', 'table-row-group', 'table-header-group', 'table-footer-group', 'table-row']);
const PACKED = new Set(['normal', 'start', 'flex-start', 'stretch']);

function touches(r: DOMRect, region: Region): boolean {
  return r.right >= region.left && r.left <= region.right && r.bottom >= region.top && r.top <= region.bottom;
}

/** Border-box size with fractions (rows of 39.5 px pinned to 40 would drift); a transformed box has only whole pixels. */
function boxSize(el: HTMLElement, style: CSSStyleDeclaration): [number, number] {
  if (style.transform && style.transform !== 'none') return [el.offsetWidth, el.offsetHeight];
  const rect = el.getBoundingClientRect();
  return [Math.round(rect.width * 100) / 100, Math.round(rect.height * 100) / 100];
}

function pin(el: Element, width: number | 'auto' | null, height: number | 'auto' | null, plan: ShotPlan): void {
  const prev = el.getAttribute(PIN_ATTR)?.split(',') ?? ['', ''];
  const w = width === null ? prev[0] : String(width);
  const h = height === null ? prev[1] : String(height);
  if (!el.hasAttribute(PIN_ATTR)) plan.pinned.push(el);
  el.setAttribute(PIN_ATTR, `${w},${h}`);
}

/** Left out of the clone: planned out, or inside a stub. */
export function cutByPlan(node: Node, plan: ShotPlan): boolean {
  if (node instanceof Element && plan.skip.has(node)) return true;
  const parent = node.parentElement;
  return !!parent && plan.stubs.has(parent);
}

const SIZE_PROPS = {
  w: ['width', 'inline-size', 'min-width', 'min-inline-size', 'max-width', 'max-inline-size'],
  h: ['height', 'block-size', 'min-height', 'min-block-size', 'max-height', 'max-block-size'],
};

function setSize(style: CSSStyleDeclaration, axis: 'w' | 'h', value: string): void {
  const [size, , min, , max] = SIZE_PROPS[axis];
  // The clone carries the computed size in both physical and logical properties: the later one would win.
  for (const name of SIZE_PROPS[axis]) style.removeProperty(name);
  if (value === 'auto') return;
  style.setProperty(size, `${value}px`);
  style.setProperty(min, `${value}px`);
  style.setProperty(max, `${value}px`);
}

/** Applies the pinned size to a cloned element (its attribute came with the clone). `auto` frees the computed size. */
export function applyPin(cloned: HTMLElement): void {
  const value = cloned.getAttribute(PIN_ATTR);
  if (value === null) return;
  cloned.removeAttribute(PIN_ATTR);
  const [w, h] = value.split(',');
  cloned.style.setProperty('box-sizing', 'border-box');
  if (w) setSize(cloned.style, 'w', w);
  if (h) setSize(cloned.style, 'h', h);
}

export function planShot(root: HTMLElement, region: Region, skip: (el: Element) => boolean): ShotPlan {
  const plan: ShotPlan = { skip: new Set(), stubs: new Set(), pinned: [], images: [] };
  const tables = new Set<Element>();

  const walk = (el: Element): boolean => {
    const rect = el.getBoundingClientRect();
    let self = touches(rect, region);
    if (rect.width === 0 && rect.height === 0 && el.getClientRects().length === 0) {
      // No box: display none (nothing to draw) or display contents (its children have the boxes).
      if (getComputedStyle(el).display === 'none') return false;
      self = false;
    }
    if (!(el instanceof HTMLElement) || WHOLE.has(el.localName)) return self;
    const kids = [...el.children].filter((kid) => !skip(kid));
    const seen = kids.map(walk);
    const any = seen.some(Boolean);
    if (!self && !any) return false;

    const own = getComputedStyle(el);
    const display = own.display;
    // Children of an inline box share its lines: cutting one of them moves the rest.
    if (display === 'inline') return true;
    // A container that spreads its children over its size would spread the kept ones over the room of the cut ones.
    const spreads = /flex|grid/.test(display) && !(PACKED.has(own.justifyContent) && PACKED.has(own.alignContent));
    const last = seen.lastIndexOf(true);
    let cutTail = false;
    kids.forEach((kid, i) => {
      if (seen[i]) return;
      const style = getComputedStyle(kid);
      if (style.display === 'none' || style.position === 'absolute' || style.position === 'fixed') {
        plan.skip.add(kid);
      } else if (!spreads && i > last && kid.getBoundingClientRect().top >= region.bottom) {
        plan.skip.add(kid);
        cutTail = true;
      } else if (kid instanceof HTMLElement && style.display !== 'inline' && style.display !== 'contents') {
        plan.stubs.add(kid);
        const [w, h] = boxSize(kid, style);
        pin(kid, w, h, plan);
      }
    });
    // The clone keeps every computed size: a table or row group without its cut rows would stretch the rest.
    if (cutTail && TABLE_PARTS.has(display)) {
      const table = el.closest('table');
      if (table) tables.add(table);
      if (display !== 'table-row') pin(el, null, 'auto', plan);
    }
    return true;
  };
  walk(root);

  for (const table of tables) {
    if (!(table instanceof HTMLTableElement)) continue;
    pin(table, boxSize(table, getComputedStyle(table))[0], 'auto', plan);
    for (const row of table.rows) {
      if (plan.stubs.has(row) || plan.skip.has(row) || (row.parentElement && (plan.skip.has(row.parentElement) || plan.stubs.has(row.parentElement)))) continue;
      for (const cell of row.cells) if (!plan.skip.has(cell) && !plan.stubs.has(cell)) pin(cell, boxSize(cell, getComputedStyle(cell))[0], null, plan);
    }
  }

  const out = (node: Element): boolean => {
    for (let at: Element | null = node; at && at !== root; at = at.parentElement) {
      if (plan.skip.has(at) || (at !== node && plan.stubs.has(at))) return true;
    }
    return false;
  };
  plan.images = [...root.querySelectorAll('img')].filter((img) => !skip(img) && !out(img));
  return plan;
}
